<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { fly } from 'svelte/transition';
  import { expoInOut } from 'svelte/easing';
  import { FilterStore, getDefaultFilters } from '$lib/utils/filters/store';
	import { LinkAction } from '$lib/components/atoms/Link';
	import { Button } from '$lib/components/atoms/Button';

  let open = false;
  let form: HTMLFormElement;
  let filtersCount = 0;

  const filterStoreInstance = FilterStore.getInstance();
  const isAnyFilterApplied = filterStoreInstance.isAnyFilterApplied;

  const unsubsribeFromFilters = filterStoreInstance.filterStore.subscribe(
    (filters) => {
      filtersCount = 0;
      const defaultFilters = getDefaultFilters();

      Object.keys(filters).forEach((key) => {
        if (
          Array.isArray(filters[key]) &&
          Array.isArray(defaultFilters[key]) &&
          !FilterStore.isEqual(filters[key], defaultFilters[key])
        ) {
          filtersCount += filters[key].length;
        } else if (
          !Array.isArray(filters[key]) &&
          !Array.isArray(defaultFilters[key]) &&
          filters[key] !== defaultFilters[key]
        ) {
          filtersCount += 1;
        }
      });
    }
  );

  $: {
    if (typeof window !== 'undefined') {
      if (open) {
        document?.body.classList.add('is-navigation-open');
      } else {
        document?.body.classList.remove('is-navigation-open');
      }
    }
  }

  const dispatch = createEventDispatcher();

  const onChange = () => {
    
    sendChangeEvent();
  };

  const sendChangeEvent = () => {
    const formData = new FormData(form);

    // Convert formData to object, if key already exists, convert value to array and push new value to it
    const formDataObject = Array.from(formData.entries()).reduce(
      (acc, [key, value]) => {
        if (acc[key]) {
          if (Array.isArray(acc[key])) {
            acc[key].push(value);
          } else {
            acc[key] = [acc[key], value];
          }
        } else {
          acc[key] = value;
        }

        return acc;
      },
      {}
    );

    dispatch('change', {
      data: formDataObject
    });
  };

  onDestroy(unsubsribeFromFilters);

  const openFilter = () => {
    open = true;
  };

  const removeAllFilters = () => {
    dispatch('removeAllFilters');
  };
</script>

<form
  class="filter"
  on:change={onChange}
  on:submit|preventDefault
  bind:this={form}
>
  {#if filtersCount > 0}
    <span class="filters-count"
      >{filtersCount}
      {filtersCount > 1 ? 'filtri applicati' : 'filtro applicato'}</span
    >
  {/if}
  <div class="filter-toggle">
    <LinkAction icon="filter" on:click={openFilter}>Filtra</LinkAction>
  </div>

  <div class="filter-content" class:open>
    <div class="filter-dropdowns">
      {#if open}
        <div class="filter-header">
          <span class="filter-header-title">Filtra per</span>

          <div class="filter-header-close">
            <Button
              variant="icon"
              color="grey"
              icon="close"
              on:click={() => (open = false)}
            />
          </div>
        </div>

        <div class="flex justify-end">
          {#if $isAnyFilterApplied}
            <LinkAction
              iconPos="end"
              icon="reset"
              on:click={() => removeAllFilters()}
            >
              Rimuovi filtri
            </LinkAction>
          {/if}
        </div>
      {/if}

      <slot />
    </div>

    {#if open}
      <div
        class="filter-footer"
        in:fly={{ y: 80, opacity: 1, duration: 500, easing: expoInOut }}
        out:fly={{ y: 0, opacity: 1, duration: 500, easing: expoInOut }}
      >
        <Button
          on:click={() => (open = false)}
          class="apply-button"
          color="grey">CHIUDI</Button
        >
      </div>
    {/if}
  </div>
</form>

<style type="text/scss" lang="scss" scoped>
  .filter {
    @apply flex items-center;
  }

  .filter-toggle {
    @apply ml-auto;
    @apply md:hidden;
  }

  .filters-count {
    @apply text-sm font-semibold;
    @apply mr-auto;
    @apply md:hidden;
  }

  .filter-content {
    @apply invisible;
    @apply fixed inset-0 pointer-events-none;
    @apply flex flex-col;
    @apply bg-white opacity-0;
    @apply transition-opacity duration-100 ease-in-out;
    @apply md:static md:pointer-events-auto;
    @apply md:visible md:opacity-100;
    @apply md:flex-grow;
    padding-top: var(--navbar-height);
    padding-bottom: var(--navbar-height);


    @screen md {
      padding-top: 0;
      padding-bottom: 0;
      max-width: 100%;
    }

    &.open {
      @apply visible;
      @apply opacity-100;
      @apply pointer-events-auto;
      @apply z-overlay md:z-auto;
    }
  }

  .filter-dropdowns {
    @apply flex flex-col;
    @apply -m-3 px-4 py-6;
    @apply overflow-y-auto;
    @apply md:flex-row md:overflow-y-visible;
    @apply md:p-0;
  }

  :global(.filter-dropdowns > *) {
    @apply m-3;
    @apply flex-1;
  }

  .filter-header {
    @apply flex justify-between;
    @apply md:hidden;
  }

  .filter-footer {
    @apply fixed inset-x-0;
    @apply flex justify-end;
    @apply px-4;
    @apply bg-white;
    @apply z-modal-overlay;
    @apply md:hidden;
  }

  .filter-header-title {
    @apply font-serif font-medium;
    @apply text-md;
  }

  .filter-header-close {
    @apply -m-2;
  }

  .filter-footer {
    @apply bottom-0;
    @apply py-2.5;
    box-shadow: 0 -2px 10px 0 rgba(0, 0, 0, 0.15);
  }
</style>
